import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import axios from "axios";
import { API } from "@/config";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PatientCombobox } from "@/components/PatientCombobox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, FileText, Eye, Info } from "lucide-react";
import { toPaise, toRupees, formatRupees } from "../utils/currency";
import PropTypes from 'prop-types';
import { useUserRole } from "../hooks/useUserRole";
import { useMode } from "@/context/ModeContext";
import { getAppointmentAccess } from "../utils/accessPolicy";

export default function InvoiceGeneration({ token, onLogout }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { memberships } = useUserRole();
    const { activeMode } = useMode();
    const doctorId = localStorage.getItem('user_id');

    // Pre-selections from URL params
    const preselectedPatientId = searchParams.get("patient");
    const preselectedAppointmentId = searchParams.get("appointment");



    // Form state
    const [selectedPatientId, setSelectedPatientId] = useState(preselectedPatientId || "");
    const [selectedAppointments, setSelectedAppointments] = useState([]);
    const [items, setItems] = useState([
        { description: "", quantity: 1, unit_price: 0 }
    ]);
    const [taxAmount, setTaxAmount] = useState(0);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [notes, setNotes] = useState("");
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewPdf, setPreviewPdf] = useState(null);

    // Load patients from local db
    const allPatients = useLiveQuery(() =>
        db.patients.filter(p => !p.deleted).toArray(),
        []);

    // Load appointments for selected patient (only those without invoices)
    const rawPatientAppointments = useLiveQuery(async () => {
        if (!selectedPatientId) return [];
        const apts = await db.appointments
            .where("patient_id")
            .equals(selectedPatientId)
            .filter(apt => apt.status === "completed" && !apt.invoice_id)
            .toArray();
        return apts.sort((a, b) => new Date(b.date_time) - new Date(a.date_time));
    }, [selectedPatientId]);

    const patientAppointments = useMemo(() => {
        if (!rawPatientAppointments) return [];
        return rawPatientAppointments.filter(apt => {
            const matchesMode = activeMode === 'personal'
                ? (!apt.clinic_id || apt.clinic_id === 'personal')
                : activeMode === 'everything'
                ? true
                : apt.clinic_id === activeMode;
            if (!matchesMode) return false;
            const access = getAppointmentAccess(apt, doctorId, memberships);
            return access.canEdit;
        });
    }, [rawPatientAppointments, activeMode, doctorId, memberships]);

    // Pre-select appointment if provided
    useEffect(() => {
        if (preselectedAppointmentId && patientAppointments?.length) {
            const apt = patientAppointments.find(a => a.id === preselectedAppointmentId);
            if (apt && !selectedAppointments.find(a => a.id === apt.id)) {
                setSelectedAppointments([apt]);
                // Auto-populate first item with appointment fee (convert from paise to rupees)
                setItems([{
                    description: `Consultation on ${new Date(apt.date_time).toLocaleDateString()}`,
                    quantity: 1,
                    unit_price: toRupees(apt.fee_amount || 0)
                }]);
            }
        }
    }, [preselectedAppointmentId, patientAppointments]);

    // Get selected patient details
    const selectedPatient = useMemo(() => {
        if (!selectedPatientId || !allPatients) return null;
        return allPatients.find(p => p.id === selectedPatientId);
    }, [selectedPatientId, allPatients]);

    // Calculate totals
    const subtotal = useMemo(() => {
        return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    }, [items]);

    const totalAmount = useMemo(() => {
        // All in paise
        return subtotal + (parseInt(taxAmount) || 0) - (parseInt(discountAmount) || 0);
    }, [subtotal, taxAmount, discountAmount]);

    // Handle item changes
    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    // Toggle appointment selection
    const toggleAppointment = (appointment) => {
        const exists = selectedAppointments.find(a => a.id === appointment.id);
        if (exists) {
            setSelectedAppointments(selectedAppointments.filter(a => a.id !== appointment.id));

            // Remove the corresponding item
            // We identify it by description matching the auto-generated one
            const desc = `Consultation on ${new Date(appointment.date_time).toLocaleDateString()}`;
            setItems(items.filter(i => i.description !== desc || i.unit_price !== toRupees(appointment.fee_amount || 0)));
        } else {
            setSelectedAppointments([...selectedAppointments, appointment]);

            const newItem = {
                description: `Consultation on ${new Date(appointment.date_time).toLocaleDateString()}`,
                quantity: 1,
                unit_price: toRupees(appointment.fee_amount || 0)
            };

            // Check if we should update an empty initial item
            if (items.length === 1 && !items[0].description && items[0].unit_price === 0) {
                setItems([newItem]);
            } else {
                setItems([...items, newItem]);
            }
        }
    };

    // Helper to convert base64 to blob
    const base64ToBlob = (base64String, contentType = 'application/pdf') => {
        try {
            const byteCharacters = atob(base64String);
            const byteArrays = [];

            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);

                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }

            const blob = new Blob(byteArrays, { type: contentType });
            return blob;
        } catch (e) {
            console.error("Blob conversion error:", e);
            return null;
        }
    };

    const [previewUrl, setPreviewUrl] = useState(null);

    // Manage preview URL lifecycle
    useEffect(() => {
        if (previewPdf) {
            const blob = base64ToBlob(previewPdf);
            if (blob) {
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
                return () => URL.revokeObjectURL(url);
            }
        } else {
            setPreviewUrl(null);
        }
    }, [previewPdf]);

    // Preview invoice
    const handlePreview = async () => {
        if (!selectedPatientId) {
            toast.error("Please select a patient");
            return;
        }
        if (selectedAppointments.some(apt => !getAppointmentAccess(apt, doctorId, memberships).canEdit)) {
            toast.error("You do not have permission to invoice selected appointment(s)");
            return;
        }
        if (items.filter(i => i.description && i.unit_price > 0).length === 0) {
            toast.error("Please add at least one item");
            return;
        }

        setIsPreviewing(true);
        try {
            const response = await axios.post(
                `${API}/invoices/preview`,
                {
                    patient_id: selectedPatientId,
                    appointment_ids: selectedAppointments.map(a => a.id),
                    items: items.filter(i => i.description).map(i => ({
                        description: i.description,
                        quantity: parseInt(i.quantity) || 1,
                        unit_price: toPaise(i.unit_price)  // Convert to paise
                    })),
                    tax_amount: toPaise(taxAmount),  // Convert to paise
                    discount_amount: toPaise(discountAmount),  // Convert to paise
                    notes,
                    issue_date: issueDate
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.pdf_available && response.data.pdf_base64) {
                setPreviewPdf(response.data.pdf_base64);
            } else {
                toast.info("PDF preview not available, showing data preview");
                // Could display JSON preview here
            }
        } catch (error) {
            console.error("Preview error:", error);
            toast.error(error.response?.data?.detail || "Failed to generate preview");
        } finally {
            setIsPreviewing(false);
        }
    };

    // Create invoice
    const handleSubmit = async () => {
        if (!selectedPatientId) {
            toast.error("Please select a patient");
            return;
        }
        if (selectedAppointments.some(apt => !getAppointmentAccess(apt, doctorId, memberships).canEdit)) {
            toast.error("You do not have permission to invoice selected appointment(s)");
            return;
        }
        if (items.filter(i => i.description && i.unit_price > 0).length === 0) {
            toast.error("Please add at least one item");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post(
                `${API}/invoices`,
                {
                    patient_id: selectedPatientId,
                    appointment_ids: selectedAppointments.map(a => a.id),
                    items: items.filter(i => i.description).map(i => ({
                        description: i.description,
                        quantity: parseInt(i.quantity) || 1,
                        unit_price: toPaise(i.unit_price)  // Convert to paise
                    })),
                    tax_amount: toPaise(taxAmount),  // Convert to paise
                    discount_amount: toPaise(discountAmount),  // Convert to paise
                    notes,
                    issue_date: issueDate
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Save to local db
            await db.invoices.put({
                ...response.data,
                _synced: true
            });

            // Update appointments locally
            for (const apt of selectedAppointments) {
                await db.appointments.update(apt.id, { invoice_id: response.data.id });
            }

            toast.success(`Invoice ${response.data.invoice_number} created successfully!`);
            navigate(`/invoices/${response.data.id}`);
        } catch (error) {
            console.error("Create error:", error);
            toast.error(error.response?.data?.detail || "Failed to create invoice");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page-container">
            <Sidebar active="invoices" onLogout={onLogout} />
            <div className="main-content">
                <div className="header">
                    <Button variant="ghost" onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </Button>
                    <h1>Generate Invoice</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
                    {/* Main Form */}
                    <div className="space-y-6">
                        {/* Patient Selection */}
                        <Card>
                            <CardHeader>
                                <CardTitle style={{ fontSize: "1rem" }}>Patient</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <PatientCombobox
                                    patients={allPatients || []}
                                    value={selectedPatientId}
                                    onChange={(id) => {
                                        setSelectedPatientId(id);
                                        setSelectedAppointments([]);
                                    }}
                                />
                                {selectedPatient && (
                                    <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "#f8fafc", borderRadius: "8px" }}>
                                        <div style={{ fontWeight: "600" }}>{selectedPatient.name}</div>
                                        <div style={{ fontSize: "0.875rem", color: "#64748b" }}>{selectedPatient.phone}</div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Appointments */}
                        {selectedPatientId && patientAppointments?.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle style={{ fontSize: "1rem" }}>
                                        Select Appointments (Optional)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                        {patientAppointments.map(apt => (
                                            <label
                                                key={apt.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.75rem",
                                                    padding: "0.75rem",
                                                    background: selectedAppointments.find(a => a.id === apt.id) ? "#eff6ff" : "#f8fafc",
                                                    borderRadius: "8px",
                                                    cursor: "pointer",
                                                    border: selectedAppointments.find(a => a.id === apt.id) ? "1px solid #3b82f6" : "1px solid transparent"
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedAppointments.find(a => a.id === apt.id)}
                                                    onChange={() => toggleAppointment(apt)}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: "500" }}>
                                                        {new Date(apt.date_time).toLocaleDateString()} - {formatRupees(apt.fee_amount)}
                                                    </div>
                                                    <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                                                        {apt.notes || "Consultation"}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Line Items */}
                        <Card>
                            <CardHeader className="flex flex-row justify-between items-center">
                                <CardTitle style={{ fontSize: "1rem" }}>Invoice Items</CardTitle>
                                <Button variant="outline" size="sm" onClick={addItem}>
                                    <Plus size={16} style={{ marginRight: "0.25rem" }} />
                                    Add Item
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                    {items.map((item, index) => (
                                        <div key={index} style={{ display: "grid", gridTemplateColumns: "2fr 80px 100px 40px", gap: "0.5rem", alignItems: "end" }}>
                                            <div>
                                                {index === 0 && <Label>Description</Label>}
                                                <Input
                                                    value={item.description}
                                                    onChange={(e) => updateItem(index, "description", e.target.value)}
                                                    placeholder="Service description"
                                                />
                                            </div>
                                            <div>
                                                {index === 0 && <Label>Qty</Label>}
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(index, "quantity", e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                {index === 0 && <Label>Unit Price (₹)</Label>}
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    placeholder="Amount in Rupees"
                                                    value={item.unit_price}
                                                    onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeItem(index)}
                                                disabled={items.length === 1}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Additional Details */}
                        <Card>
                            <CardHeader>
                                <CardTitle style={{ fontSize: "1rem" }}>Additional Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                                    <div>
                                        <Label>Issue Date</Label>
                                        <Input
                                            type="date"
                                            value={issueDate}
                                            onChange={(e) => setIssueDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label>Tax Amount (₹)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="1"
                                            placeholder="Amount in Rupees"
                                            value={taxAmount}
                                            onChange={(e) => setTaxAmount(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Label>Discount (₹)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="1"
                                            placeholder="Amount in Rupees"
                                            value={discountAmount}
                                            onChange={(e) => setDiscountAmount(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Notes</Label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Additional notes..."
                                        rows={3}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Summary Sidebar */}
                    <div className="space-y-6 sticky top-4 h-fit self-start">
                        <Card>
                            <CardHeader>
                                <CardTitle style={{ fontSize: "1rem" }}>Summary</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.5rem" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span>Subtotal</span>
                                        <span>{formatRupees(toPaise(subtotal))}</span>
                                    </div>
                                    {taxAmount > 0 && (
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span>Tax</span>
                                            <span>{formatRupees(toPaise(taxAmount))}</span>
                                        </div>
                                    )}
                                    {discountAmount > 0 && (
                                        <div style={{ display: "flex", justifyContent: "space-between", color: "#16a34a" }}>
                                            <span>Discount</span>
                                            <span>-{formatRupees(toPaise(discountAmount))}</span>
                                        </div>
                                    )}
                                    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "600", fontSize: "1.125rem", borderTop: "1px solid #e2e8f0", paddingTop: "0.5rem" }}>
                                        <span>Total</span>
                                        <span>{formatRupees(toPaise(totalAmount))}</span>
                                    </div>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                    <Button
                                        variant="outline"
                                        onClick={handlePreview}
                                        disabled={isPreviewing || !selectedPatientId}
                                        style={{ width: "100%" }}
                                    >
                                        <Eye size={16} style={{ marginRight: "0.5rem" }} />
                                        {isPreviewing ? "Generating..." : "Preview"}
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || !selectedPatientId}
                                        style={{ width: "100%", background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)" }}
                                    >
                                        <FileText size={16} style={{ marginRight: "0.5rem" }} />
                                        {isSubmitting ? "Creating..." : "Create Invoice"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Settings Tip */}
                        <div style={{
                            padding: "0.75rem 1rem",
                            background: "#f0f9ff",
                            borderRadius: "8px",
                            border: "1px solid #bae6fd",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.5rem"
                        }}>
                            <Info size={16} style={{ color: "#0284c7", marginTop: "2px", flexShrink: 0 }} />
                            <p style={{ fontSize: "0.8rem", color: "#0369a1", margin: 0, lineHeight: 1.4 }}>
                                Want your registration number, signature, or stamp on invoices?
                                <Link to="/settings" style={{ fontWeight: 600, marginLeft: "4px", color: "#0284c7" }}>
                                    Update in Settings
                                </Link>
                            </p>
                        </div>

                        {/* PDF Preview */}
                        {previewUrl && (
                            <Card>
                                <CardHeader style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                                    <CardTitle style={{ fontSize: "1rem" }}>Preview</CardTitle>
                                    <Button variant="ghost" size="sm" onClick={() => window.open(previewUrl, '_blank')}>
                                        <Eye size={16} style={{ marginRight: "0.5rem" }} />
                                        Fullscreen
                                    </Button>
                                </CardHeader>
                                <CardContent>
                                    <iframe
                                        src={previewUrl}
                                        style={{ width: "100%", height: "600px", border: "1px solid #e2e8f0", borderRadius: "8px" }}
                                        title="Invoice Preview"
                                    />
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

InvoiceGeneration.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
};
